import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useConsultaModal } from '../../context/ConsultaModalContext';
import { trackEvent, trackConversion } from '../../lib/analytics';

const AREAS = [
    'Compra e Venda de Imóveis',
    'Usucapião',
    'Inventário',
    'Divórcio Extrajudicial',
    'Doação de Imóveis',
    'Produtos Bancários',
    'Contencioso Cível',
    'Outra área',
];

const INITIAL_FORM = { name: '', phone: '', email: '', area: '', message: '' };

const WHATSAPP_NUMBER = '5511969241378';

const buildWhatsAppUrl = (form: typeof INITIAL_FORM) => {
    const lines = [
        'Olá! Gostaria de agendar uma consulta.',
        '',
        `*Nome:* ${form.name}`,
        `*Telefone:* ${form.phone}`,
        `*E-mail:* ${form.email}`,
        `*Área:* ${form.area || 'Não informada'}`,
        form.message ? `*Resumo:* ${form.message}` : null,
    ].filter(Boolean).join('\n');

    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines)}`;
};

export const ConsultaModal = () => {
    const { isOpen, preSelectedArea, close } = useConsultaModal();
    const [form, setForm] = useState(INITIAL_FORM);
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [waUrl, setWaUrl] = useState('');

    useEffect(() => {
        if (isOpen) {
            setForm({ ...INITIAL_FORM, area: preSelectedArea });
            setStatus('idle');
        }
    }, [isOpen, preSelectedArea]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', onKey);
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [isOpen, close]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const url = buildWhatsAppUrl(form);
        setWaUrl(url);
        // Só generate_lead aqui — é A conversão do formulário. Não disparar
        // conversion_click{method:form} junto (duplicaria a conversão no GA4/Ads).
        trackEvent('generate_lead', { form_id: 'consulta_modal', area: form.area || 'nao_informada' });
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        setStatus(win ? 'success' : 'error');
    };

    const field = (id: string, value: string) =>
        setForm(prev => ({ ...prev, [id]: value }));

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={close}
                    />

                    {/* Modal container */}
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 pointer-events-none">
                        <motion.div
                            className="relative w-full sm:max-w-lg bg-[#0A0A0A] border border-stone-800 shadow-2xl pointer-events-auto max-h-[92vh] overflow-y-auto sm:rounded-none"
                            initial={{ y: 80, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 80, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Gold top accent line */}
                            <div className="h-[2px] bg-pacces-gold w-full" />

                            {/* Header */}
                            <div className="border-b border-stone-800 px-8 py-6 flex items-start justify-between">
                                <div>
                                    <h2 className="text-xl font-bold text-white uppercase tracking-widest font-vanguard-display">
                                        Agendar Consulta
                                    </h2>
                                    <p className="text-[12px] font-bold text-pacces-gold uppercase tracking-[0.25em] mt-1">
                                        Atendimento Confidencial
                                    </p>
                                </div>
                                <button
                                    onClick={close}
                                    className="text-stone-500 hover:text-white transition-colors mt-0.5 p-1"
                                    aria-label="Fechar modal"
                                >
                                    <span className="material-icons text-xl">close</span>
                                </button>
                            </div>

                            {/* Body */}
                            <div className="px-8 py-8">
                                {status === 'success' ? (
                                    <motion.div
                                        className="text-center py-10"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <span className="material-icons text-pacces-gold text-5xl mb-5 block">check_circle</span>
                                        <h3 className="text-white font-bold text-lg mb-3 font-vanguard-display uppercase tracking-widest">
                                            Solicitação Recebida
                                        </h3>
                                        <p className="text-stone-400 text-sm leading-relaxed max-w-xs mx-auto">
                                            O WhatsApp foi aberto com sua mensagem. Caso não abriu automaticamente,
                                            entre em contato pelo número (11) 96924-1378.
                                        </p>
                                        <button
                                            onClick={close}
                                            className="mt-8 text-pacces-gold text-[12px] font-bold uppercase tracking-widest border-b border-pacces-gold/40 hover:border-pacces-gold pb-1 transition-all"
                                        >
                                            Fechar
                                        </button>
                                    </motion.div>
                                ) : status === 'error' ? (
                                    <div className="text-center py-10">
                                        <span className="material-icons text-pacces-gold text-5xl mb-5 block">chat</span>
                                        <h3 className="text-white font-bold text-lg mb-3 font-vanguard-display uppercase tracking-widest">
                                            Falta um passo
                                        </h3>
                                        <p className="text-stone-400 text-sm leading-relaxed max-w-xs mx-auto">
                                            Não conseguimos abrir o WhatsApp automaticamente. Toque no botão abaixo para enviar sua mensagem, ou ligue para (11) 96924-1378.
                                        </p>
                                        <a
                                            href={waUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => trackConversion('sp', 'whatsapp')}
                                            className="mt-7 inline-flex items-center justify-center gap-2 bg-pacces-gold text-pacces-ebony font-bold px-8 py-4 text-[12px] tracking-[0.2em] uppercase hover:bg-white transition-all duration-300"
                                        >
                                            <span className="material-icons text-sm">chat</span>
                                            Abrir o WhatsApp
                                        </a>
                                        <button
                                            onClick={close}
                                            className="block mx-auto mt-6 text-stone-500 text-[12px] font-bold uppercase tracking-widest hover:text-white transition-colors"
                                        >
                                            Fechar
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                            <div>
                                                <label className="block text-[12px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                                    Nome Completo <span className="text-pacces-gold">*</span>
                                                </label>
                                                <input
                                                    required
                                                    type="text"
                                                    placeholder="Seu nome"
                                                    value={form.name}
                                                    onChange={e => field('name', e.target.value)}
                                                    disabled={status === 'loading'}
                                                    className="w-full bg-stone-900 border border-stone-800 text-white text-base focus:border-pacces-gold transition-colors placeholder:text-stone-700 px-4 py-3 outline-none disabled:opacity-50"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[12px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                                    Telefone / WhatsApp <span className="text-pacces-gold">*</span>
                                                </label>
                                                <input
                                                    required
                                                    type="tel"
                                                    placeholder="(11) 9xxxx-xxxx"
                                                    value={form.phone}
                                                    onChange={e => field('phone', e.target.value)}
                                                    disabled={status === 'loading'}
                                                    className="w-full bg-stone-900 border border-stone-800 text-white text-base focus:border-pacces-gold transition-colors placeholder:text-stone-700 px-4 py-3 outline-none disabled:opacity-50"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-[12px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                                E-mail <span className="text-pacces-gold">*</span>
                                            </label>
                                            <input
                                                required
                                                type="email"
                                                placeholder="email@empresa.com"
                                                value={form.email}
                                                onChange={e => field('email', e.target.value)}
                                                disabled={status === 'loading'}
                                                className="w-full bg-stone-900 border border-stone-800 text-white text-base focus:border-pacces-gold transition-colors placeholder:text-stone-700 px-4 py-3 outline-none disabled:opacity-50"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[12px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                                Área de Interesse
                                            </label>
                                            <div className="relative">
                                                <select
                                                    value={form.area}
                                                    onChange={e => field('area', e.target.value)}
                                                    disabled={status === 'loading'}
                                                    className="w-full appearance-none bg-stone-900 border border-stone-800 text-white text-base focus:border-pacces-gold transition-colors px-4 py-3 outline-none disabled:opacity-50 pr-10"
                                                >
                                                    <option value="">Selecione uma área...</option>
                                                    {AREAS.map(a => (
                                                        <option key={a} value={a}>{a}</option>
                                                    ))}
                                                </select>
                                                <span className="material-icons absolute right-3 top-1/2 -translate-y-1/2 text-stone-600 text-base pointer-events-none">
                                                    expand_more
                                                </span>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-[12px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                                Resumo do Caso{' '}
                                                <span className="text-stone-700 normal-case tracking-normal">(opcional)</span>
                                            </label>
                                            <textarea
                                                rows={3}
                                                placeholder="Descreva brevemente sua situação..."
                                                value={form.message}
                                                onChange={e => field('message', e.target.value)}
                                                disabled={status === 'loading'}
                                                className="w-full bg-stone-900 border border-stone-800 text-white text-base focus:border-pacces-gold transition-colors placeholder:text-stone-700 px-4 py-3 outline-none resize-none disabled:opacity-50"
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            className="w-full bg-pacces-gold text-pacces-ebony font-bold py-5 text-[12px] tracking-[0.3em] uppercase hover:bg-white transition-all duration-300 shadow-xl flex items-center justify-center gap-3"
                                        >
                                            <span className="material-icons text-sm">send</span>
                                            Continuar no WhatsApp
                                        </button>

                                        <p className="text-[12px] text-stone-600 text-center leading-relaxed">
                                            Suas informações são tratadas com total sigilo e confidencialidade.
                                        </p>
                                    </form>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};
